import { assoc } from 'ramda'
import { shouldRenderReply, checkReplyValidation } from '@btime/helpers'

// Mesmo helper usado pela execução individual/dinâmica (`ReplyInput`,
// autofill) para decidir se uma resposta JÁ tem valor de fato. Diferente de
// `checkReplyValidation().isValid` (que também exige campos secundários como
// foto/nota obrigatórios), `hasReplyValue` olha só para `value` e entende as
// formas que ele assume por tipo — string, objeto (assinatura), array (bloco)
// — então um bloco sem nenhuma sub-resposta ([]) não é confundido com
// "respondido", e uma assinatura já salva (objeto não vazio) é reconhecida
// como respondida mesmo que alguma validação secundária ainda esteja pendente.
import { hasReplyValue } from '../../../../../containers/dynamic/form/_form/formatRepliesFormData'

export { hasReplyValue }

// Tipos de pergunta tratados como "campo largo": não cabem numa célula da
// grade (foto, assinatura, geolocalização, bloco de sub-perguntas, e outros
// inputs que abrem seu próprio painel/modal hoje). A célula mostra um botão
// que abre o editor completo — o mesmo DynamicInput usado na execução
// individual — para aquela atividade+pergunta específica.
export const WIDE_QUESTION_TYPES = new Set([
  'gallery', // foto
  'sign', // assinatura (com documento anexado)
  'signature', // assinatura (perfil)
  'coordinates', // geolocalização
  'block', // sub-perguntas (bloco repetível)
  'stock', // estoque (busca + operação + quantidade)
  'action_plan', // plano de ação (abre modal próprio)
  'integration' // seleção de integração externa (Omie/Nibo)
])

// Tipos sem resposta de fato (texto informativo) — não aparecem como coluna,
// igual ao comportamento de `DynamicInput` hoje (renderiza `false`).
const NON_ANSWERABLE_TYPES = new Set(['informative'])

export const isBlockChildQuestion = question => !!question?.parentReference

export const isGridColumnQuestion = question =>
  !!question && !isBlockChildQuestion(question) && !NON_ANSWERABLE_TYPES.has(question.type?.name)

export const isWideQuestion = question => WIDE_QUESTION_TYPES.has(question?.type?.name)

const getChildQuestions = (parentReference, allQuestions) =>
  allQuestions
    .filter(question => question.parentReference === parentReference)
    .reduce((finalQuestions, question) => [...finalQuestions, question, ...getChildQuestions(question.reference, allQuestions)], [])

// Anexa `blockQuestions` nas perguntas do tipo bloco — igual ao que
// `formatRepliesFormData.formatBlockQuestions` faz na tela individual — para
// o `DynamicBlock` saber quais sub-perguntas renderizar dentro do editor.
export const withBlockQuestions = questions =>
  (questions || []).map(question =>
    question.type?.name === 'block'
      ? { ...question, blockQuestions: getChildQuestions(question.reference, questions) }
      : question
  )

// As colunas da grade: uma por pergunta de topo (sem contar sub-perguntas de
// bloco, que são editadas dentro do painel da própria pergunta-bloco).
export const gridColumns = questions => withBlockQuestions(questions).filter(isGridColumnQuestion)

const groupByBlockIndex = replies => {
  const groups = {}
  replies.forEach(reply => {
    const index = reply.blockIndex ?? 0
    groups[index] = groups[index] || []
    groups[index].push(reply)
  })
  return Object.keys(groups)
    .sort((a, b) => Number(a) - Number(b))
    .map(key => groups[key])
}

// Mesma lógica de `formatBlockReplies` da tela individual: agrupa as
// respostas já salvas das sub-perguntas de um bloco por `blockIndex`.
const buildBlockValue = (question, existingReplies) => {
  const childIds = (question.blockQuestions || []).map(child => child.id)
  return groupByBlockIndex((existingReplies || []).filter(reply => childIds.includes(reply.questionId)))
}

// Monta o conjunto inicial de "replies" de UMA atividade para as colunas da
// grade. Diferente do antigo `mergeBulkReplies`, aqui NÃO existe mais
// resposta em branco por divergência: cada linha mostra a resposta real
// daquela atividade específica.
export const buildRowReplies = (questions, serviceOrder) => {
  const columns = gridColumns(questions)
  const existingReplies = serviceOrder?.replies || []

  const base = columns.map(question => {
    if (question.type?.name === 'block') {
      return {
        questionId: question.id,
        question,
        type: 'block',
        value: buildBlockValue(question, existingReplies),
        documents: []
      }
    }

    const existing = existingReplies.find(reply => reply.questionId === question.id)

    return {
      questionId: question.id,
      question,
      type: question.type?.name,
      value: existing ? existing.value : question.defaultValue ?? undefined,
      notes: existing?.notes,
      documents: existing?.documents || []
    }
  })

  const withShouldRender = base.map(reply => assoc('shouldRender', shouldRenderReply(reply, base), reply))
  return withShouldRender.map(reply => assoc('validations', checkReplyValidation(reply), reply))
}

export const findRowReply = (rowReplies, questionId) => (rowReplies || []).find(reply => reply.questionId === questionId)

// A primeira resposta obrigatória, visível (shouldRender) e sem valor válido
// dentro de UMA linha — usada para bloquear a conclusão em massa igual à
// validação de obrigatoriedade da execução individual.
export const firstInvalidRowReply = rowReplies =>
  (rowReplies || []).find(reply => reply.shouldRender && !reply.validations?.isValid)
